import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "lib/formik";
import { useLocation, useNavigate } from "react-router";
import { addRoleSchemas } from "components/Common/validation";
import { useDispatch, useSelector } from "react-redux";
import { addCampaing } from "store/actions/cityAction";
import { getAllGiftCardCategories } from "store/actions/giftCardCategoriesAction";
import Spinners from "components/Shared/Spinners";
import Breadcrumbs from "components/Common/Breadcrumb";
import { Card, CardBody, CardTitle, Col, Row, Button } from "reactstrap";
import BackButton from "components/Shared/BackButton";
import { useTranslation } from "react-i18next";
import TextError from "components/Shared/TextError";
import { showAlert } from "store/reducers/alertReducer";
import Select from "react-select";

const initialValues = {
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
  category: "",
};

const AddCampaign = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Gift Card Deep Link "
  )}`;

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const dispatch = useDispatch();
  const { loading, error, isLoading, message, actionType, roleDetails } = useSelector((state) => state.Roles);
  const { categories } = useSelector((state) => state.GiftCardCategories);

  const [formValues, setFormValues] = useState(initialValues);
  const [roleStatus, setRoleStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    // Fetch categories when component mounts
    dispatch(getAllGiftCardCategories({
      perPage: 1000, // Get all categories
      pageNo: 1,
      active: null,
      orderBy: 1,
      keyword: "",
    }));
  }, []);

  const handleCategorySelect = (selectedOption, setFieldValue) => {
    setSelectedCategory(selectedOption);
    setFieldValue('category', selectedOption?.value || "");
  };

  const getOldValue = () => {
    const { name, nameAr, description, descriptionAr, status } = roleDetails;
    setFormValues({
      ...formValues,
      name,
      nameAr,
      description,
      descriptionAr,
    });
    setRoleStatus(status);
  };

  const handleSubmit = (values) => {
    const { name, nameAr, description, descriptionAr, category } = values;
  
    const payload = {
      name,
      nameAr,
      description,
      descriptionAr,
      categoryId: category || "", // Use the form value directly
      type: "GIFT_CARDS_LIST_VIEW",
    };
  
    // If adding a new campaign
    if (mode === "add") {
      dispatch(addCampaing(payload))
        .then((action) => {
          const { statusCode, status, msg } = action?.payload;
  
          if (statusCode === 200 && status) {
            // Show success alert and navigate
            dispatch(showAlert({ text: msg, type: "success" }));
            navigate("/Giftcard-Deep-Link"); // Navigate to Campaign page
          } else {
            // Show error if status is not successful
            dispatch(showAlert({ text: msg, type: "error" }));
          }
        })
        .catch((error) => {
          // Handle any errors that occur during the dispatch
          console.error("Error adding campaign:", error);
          dispatch(showAlert({ text: "An error occurred while adding the campaign.", type: "error" }));
        });
    }
    // Handle edit mode logic here if required
  };

  if (mode === "edit" && loading) {
    return <Spinners />;
  }

  const categoryOptions = categories?.map(category => ({
    value: category._id,
    label: category.cardType
  })) || [];

  return (
    <div className="page-content add_roles_page">
      <Breadcrumbs />
      <Row>
        <Col xl={12}>
          <Formik
            enableReinitialize={true}
            initialValues={formValues}
            validationSchema={addRoleSchemas}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue }) => (
              <Form action="">
                <Card>
                  <CardBody>
                    <CardTitle className="d-flex align-items-center">
                      <BackButton />
                      {mode === "add" ? t(" Add Gift Card Deep Link") : t("Edit Gift Card Deep Link")}
                    </CardTitle>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="name">
                            <span className="text-danger">*</span>{" "}
                            {t("Name(English)")}
                          </label>
                          <Field
                            type="text"
                            id="name"
                            name="name"
                            placeholder={t("Name(English)")}
                            className={`form-control ${
                              touched.name && errors.name ? "is-invalid" : ""
                            }`}
                          />
                          <TextError name="name" />
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="nameAr">
                            <span className="text-danger">*</span>{" "}
                            {t("Name(عربي)")}
                          </label>
                          <Field
                            type="text"
                            style={{ direction: "rtl" }}
                            id="nameAr"
                            name="nameAr"
                            placeholder={t("Name(عربي)")}
                            className={`form-control ${
                              touched.nameAr && errors.nameAr
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="nameAr" />
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="description">
                            {t("Description(English)")}
                          </label>
                          <Field
                            as="textarea"
                            id="description"
                            name="description"
                            rows={1}
                            placeholder={t("Description(English)")}
                            className={`form-control`}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="descriptionAr">
                            {t("Description(عربي)")}
                          </label>
                          <Field
                            as="textarea"
                            rows={1}
                            style={{ direction: "rtl" }}
                            id="descriptionAr"
                            name="descriptionAr"
                            placeholder={t("Description(عربي)")}
                            className={`form-control ${
                              touched.descriptionAr && errors.descriptionAr
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="descriptionAr" />
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="category">
                            <span className="text-danger">*</span>{" "}
                            {t("Category")}
                          </label>
                          <Select
                            value={selectedCategory}
                            onChange={(option) => handleCategorySelect(option, setFieldValue)}
                            options={categoryOptions}
                            placeholder={t("Select Category")}
                            className="select2-selection"
                            classNamePrefix="select2"
                            isClearable
                            isSearchable
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: '38px',
                                height: '38px',
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: '38px',
                                padding: '0 8px'
                              }),
                              input: (base) => ({
                                ...base,
                                margin: '0px',
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                height: '38px',
                              }),
                            }}
                          />
                          <Field type="hidden" name="category" />
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <div className="col-12 mt-4">
                      <div className="d-flex align-items-center justify-content-end">
                        <Button
                          type="button"
                          color="secondary"
                          className=" mx-2"
                          onClick={() => navigate(-1)}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button
                          color="success"
                          type="submit"
                          style={{ width: "fit-content" }}
                          disabled={isLoading}
                        >
                          {mode === "add" ? t("Add") : t("Save")}
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
};

export default AddCampaign;
